import { cn } from "@/utils/cn";
import { useState, useRef, useEffect } from "react";

interface PencomOTPFieldProps {
  id: string;
  label: string;
  length?: number;
  value: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  masked?: boolean;
  maskChar?: string;
  bg?: string;
  maskDelay?: number;
  error?: boolean; // New prop for error state
  helperText?: string; // New prop for displaying error messages
}

const PencomOTPField = ({
  id,
  label,
  length = 6,
  value,
  onChange,
  className,
  masked = false,
  maskChar = "⬤",
  maskDelay = 1000,
  error,
  helperText,
  bg,
}: PencomOTPFieldProps) => {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(""));
  const [displayValues, setDisplayValues] = useState<string[]>(
    new Array(length).fill("")
  );
  const [unmaskedIndex, setUnmaskedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const maskTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (value) {
      const otpArray = value.split("").slice(0, length);
      while (otpArray.length < length) {
        otpArray.push("");
      }
      setOtp(otpArray);

      // Update display values based on masking
      if (masked) {
        const displayArray = otpArray.map((val, index) =>
          val && index !== unmaskedIndex ? maskChar : val
        );
        setDisplayValues(displayArray);
      } else {
        setDisplayValues(otpArray);
      }
    }
  }, [value, length, masked, maskChar, unmaskedIndex]);

  // Effect to handle masking delay
  useEffect(() => {
    if (masked && unmaskedIndex !== null) {
      if (maskTimeoutRef.current) {
        clearTimeout(maskTimeoutRef.current);
      }

      maskTimeoutRef.current = setTimeout(() => {
        setUnmaskedIndex(null);
        setDisplayValues((prev) => prev.map((val) => (val ? maskChar : val)));
      }, maskDelay);
    }

    return () => {
      if (maskTimeoutRef.current) {
        clearTimeout(maskTimeoutRef.current);
      }
    };
  }, [unmaskedIndex, masked, maskDelay, maskChar]);

  const handleChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    // Update display values
    if (masked) {
      const newDisplayValues = [...displayValues];
      newDisplayValues[index] = element.value;
      setDisplayValues(newDisplayValues);

      // Show the newly entered character briefly
      if (element.value) {
        setUnmaskedIndex(index);
      }
    } else {
      const newDisplayValues = [...displayValues];
      newDisplayValues[index] = element.value;
      setDisplayValues(newDisplayValues);
    }

    onChange?.({
      target: { value: newOtp.join("") },
    } as React.ChangeEvent<HTMLInputElement>);

    if (element.value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    const pasteArray = paste.split("").slice(0, length);

    const newOtp = [...otp];
    const newDisplayValues = [...displayValues];

    pasteArray.forEach((char, index) => {
      if (!isNaN(Number(char)) && index < length) {
        newOtp[index] = char;
        newDisplayValues[index] = masked ? maskChar : char;
      }
    });

    setOtp(newOtp);
    setDisplayValues(newDisplayValues);
    onChange?.({
      target: { value: newOtp.join("") },
    } as React.ChangeEvent<HTMLInputElement>);

    // When pasting and masked, show all characters briefly then mask them
    if (masked && pasteArray.length > 0) {
      const lastFilledIndex =
        newOtp
          .map((val, index) => (val !== "" ? index : -1))
          .filter((index) => index !== -1)
          .pop() ?? -1;
      setUnmaskedIndex(lastFilledIndex);
    }

    // Focus the last filled input or the first empty one
    const lastFilledIndex =
      newOtp
        .map((val, index) => (val !== "" ? index : -1))
        .filter((index) => index !== -1)
        .pop() ?? -1;
    const focusIndex =
      lastFilledIndex < length - 1 ? lastFilledIndex + 1 : lastFilledIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className={cn("flex flex-col gap-y-2", className)}>
      <label
        htmlFor={id}
        className="font-clash-display text-medium-dark-grey text-xs font-semibold"
      >
        {label}
      </label>
      <div className="flex gap-x-4">
        {displayValues.map((data, index) => {
          const isMaskedDisplay = masked && data !== "" && data === maskChar;

          return (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={data}
              onChange={(e) => handleChange(e.target, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className={cn(
                "w-16 h-12 text-center bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-button focus:border-transparent leading-none font-clash-display text-black-dark-grey",
                {
                  "border-red-500": error,
                  "border-stroke": !error,
                  "text-3xl font-black": isMaskedDisplay,
                  "text-xl font-semibold": !isMaskedDisplay,
                  [bg ?? ""]: true,
                }
              )}
              autoComplete="one-time-code"
            />
          );
        })}
      </div>
      {helperText && <p className="text-red-500 text-md mt-1 font-clash-display">{helperText}</p>}
    </div>
  );
};

export default PencomOTPField;
